<template>
    <div class="max-w-7xl mx-auto md:px-4 sm:px-6 md:px-8">
        <SmartBreadcrumbs
            type="InertiaLink"
            :pages="[
                {
                    name: $tc('messages.teachers', 2),
                    href: meta.links.index,
                },
            ]"
        />
        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div>
                <CardPanel class="mt-5">
                    <img
                        :src="teacher.avatar_or_default"
                        alt="photo"
                        class="mx-auto rounded-full h-32 w-32 border-4 border-white"
                    />

                    <div class="flex flex-col justify-center items-center">
                        <div class="text-lg leading-6 font-medium text-gray-900">
                            {{ teacher.name_or_nickname }}
                        </div>
                        <div class="leading-6 font-base text-gray-600">
                            {{ $tc('messages.teachers', 1) }}
                        </div>
                    </div>
                </CardPanel>

                <CardPanel class="mt-5">
                    <div class="flex flex-col space-y-2 items-start">
                        <AppButton :href="meta.links.impersonate" desired-action v-if="meta.can.impersonate">
                            <svg
                                class="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                                ></path>
                            </svg>

                            {{ $t('messages.impersonate') }}
                        </AppButton>
                        <AppButton :href="meta.links.subjects_edit" v-if="meta.can.edit_categories">
                            {{ $tc('messages.subject', 2) }}
                        </AppButton>
                    </div>
                </CardPanel>

                <CardPanel class="mt-5">
                    <h3>
                        {{ $t('messages.photo') }}
                    </h3>

                    <div v-if="teacher.mainProfilePhoto">
                        {{ $t('messages.delete_photo') }}

                        <InertiaSquareConfirmActionButton
                            :href="meta.links.avatar_delete"
                            danger
                            :confirm-text="$t('messages.delete')"
                            method="delete"
                            :title="$t('messages.delete_photo')"
                        >
                            <template v-slot:icon>
                                <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </template>
                            {{ $t('messages.are_you_sure?') }}
                        </InertiaSquareConfirmActionButton>
                    </div>
                    <div v-else>
                        <div>
                            {{ $tc('messages.upload_photo', 2) }}
                        </div>
                        <AvatarUploader :endpoint="meta.links.avatar_store" />
                    </div>

                    <hr class="my-5" />

                    <h3>
                        {{ $t('messages.signature') }}
                    </h3>

                    <div v-if="teacher.signature">
                        <div>
                            <img :src="teacher.signature" class="w-full" :alt="$t('messages.signature')" />
                        </div>

                        {{ $t('messages.delete') }}

                        <InertiaSquareConfirmActionButton
                            :href="meta.links.signature_delete"
                            danger
                            :confirm-text="$t('messages.delete')"
                            method="delete"
                            :title="$t('messages.delete')"
                        >
                            <template v-slot:icon>
                                <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </template>
                            {{ $t('messages.are_you_sure?') }}
                        </InertiaSquareConfirmActionButton>
                    </div>
                    <div v-else>
                        <UploadImageInlineForm :url="meta.links.signature_store" />
                    </div>
                </CardPanel>
            </div>

            <div class="mt-5 md:mt-0 md:col-span-2">
                <CardPanel class="mt-5">
                    <table class="min-w-full divide-y divide-gray-200">
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.first_name') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.first_name }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.last_names') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.last_name }}
                                    {{ teacher.second_last_name }}
                                </td>
                            </tr>
                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.date_of_birth') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.date_of_birth_readable }}
                                    <span v-if="teacher.is_birthday">🎂 </span>
                                    ({{ teacher.next_birthday }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.gender') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.gender_display }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.mother_tongue') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.mother_tongue }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.country_of_origin') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.country_of_origin }}
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.group') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <AppButton :href="teacher.group.url" v-if="teacher.group">
                                        {{ teacher.group.name }}
                                    </AppButton>
                                    <template v-else>
                                        {{ $t('messages.is_not_a_tutor') }}
                                    </template>
                                </td>
                            </tr>

                            <tr>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    {{ $t('messages.email') }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.email }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </CardPanel>
            </div>
        </div>
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import CardPanel from '@/Shared/CardPanel.vue';
import AppButton from '@/components/AppButton.vue';
import AvatarUploader from '@/components/pageSpecific/AvatarUploader.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import UploadImageInlineForm from '@/components/UploadImageInlineForm.vue';

export default {
    components: {
        CardPanel,
        AppButton,
        AvatarUploader,
        InertiaSquareConfirmActionButton,
        UploadImageInlineForm,
    },
    layout: Layout,
    name: 'Show',
    props: ['teacher', 'meta', 'options', 'attendance'],
};
</script>
